import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, Lock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showToast } = useToast();

  const [formData, setFormData] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormError('');
  };

  const handleKeyEvent = (e) => {
    if (typeof e.getModifierState === 'function') {
      setCapsLock(e.getModifierState('CapsLock'));
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.username.trim()) next.username = '아이디를 입력해주세요.';
    if (!formData.password) next.password = '비밀번호를 입력해주세요.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      // Mock admin login: admin / admin123 (admin@example.com 계정과 동일 권한)
      if (formData.username === 'admin' && formData.password === 'admin123') {
        const adminUser = {
          id: 'admin',
          name: 'Administrator',
          email: 'admin@example.com',
          role: 'admin',
        };
        localStorage.setItem('user', JSON.stringify(adminUser));
        if (rememberMe) {
          localStorage.setItem('adminRememberMe', 'true');
        } else {
          localStorage.removeItem('adminRememberMe');
        }
        // AuthProvider 의 login 으로도 시도(실데이터 계정과 일치 시 컨텍스트 동기화)
        await login('admin@example.com', 'admin123').catch(() => {});
        showToast('관리자 로그인 성공!', 'success');
        navigate('/admin/dashboard', { replace: true });
        return;
      }
      setFormError('아이디 또는 비밀번호가 올바르지 않습니다.');
      showToast('아이디 또는 비밀번호가 올바르지 않습니다.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="rounded-lg bg-white p-8 shadow-lg">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-charcoal text-ivory">
              <Lock className="h-7 w-7" />
            </div>
            <h1 className="mb-2 text-3xl font-bold text-gray-900">관리자 로그인</h1>
            <p className="text-sm text-gray-600">관리자 계정으로 로그인하세요</p>
          </div>

          {/* 폼 상단 인라인 에러(aria-live) */}
          <div aria-live="polite">
            {formError && (
              <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{formError}</span>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <Input
              label="아이디"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="admin"
              error={errors.username}
              required
            />

            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-primary">
                비밀번호<span className="ml-1 text-red-600">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onKeyUp={handleKeyEvent}
                  onKeyDown={handleKeyEvent}
                  placeholder="비밀번호를 입력하세요"
                  className={`w-full rounded-lg border bg-white px-4 py-2 pr-10 text-primary placeholder-accent-400 transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2 ${
                    errors.password
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-accent-300 focus:ring-primary-500'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-accent-500 hover:text-primary"
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
              {capsLock && (
                <p className="mt-1 flex items-center gap-1 text-xs text-yellow-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Caps Lock 이 켜져 있습니다.
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              로그인 상태 유지
            </label>

            <Button type="submit" variant="primary" fullWidth disabled={submitting}>
              {submitting ? '로그인 중...' : '로그인'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">테스트 계정: admin / admin123</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
